package com.mgacreative.hotel.feature.restaurant.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.mgacreative.hotel.domain.model.PosItem
import com.mgacreative.hotel.shared.ui.designsystem.components.LanguageSelectorDropdown
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RestaurantPosScreen(
    onNavigateBack: () -> Unit = {},
    viewModel: PosViewModel = koinViewModel()
) {
    val categories by viewModel.categories.collectAsState()
    val items by viewModel.items.collectAsState()
    val cart by viewModel.cart.collectAsState()
    val paymentState by viewModel.paymentState.collectAsState()
    val activeReservations by viewModel.activeReservations.collectAsState()
    val openOrders by viewModel.openOrders.collectAsState()
    val currentOrderId by viewModel.currentOrderId.collectAsState()

    var selectedCategoryId by remember { mutableStateOf<String?>("ALL") }
    var tableName by remember { mutableStateOf("Masa 1") }

    var showTableNameDialog by remember { mutableStateOf(false) }
    var showPaymentDialog by remember { mutableStateOf(false) }
    var showManualItemDialog by remember { mutableStateOf(false) }
    var showAdisyonDialog by remember { mutableStateOf(false) }
    var showNewTableDialog by remember { mutableStateOf(false) }
    var showTopMenu by remember { mutableStateOf(false) }

    var paymentMethod by remember { mutableStateOf("CASH") } // "CASH", "ROOM"
    var selectedReservationId by remember { mutableStateOf<String?>(null) }
    var menuSearchQuery by remember { mutableStateOf("") }

    val snackbarHostState = remember { SnackbarHostState() }

    val paymentSuccessMsg = l10n("Ödeme başarıyla tamamlandı ve adisyon kapatıldı.")
    LaunchedEffect(paymentState) {
        when (val state = paymentState) {
            is PaymentState.Success -> {
                showPaymentDialog = false
                showAdisyonDialog = false
                tableName = "Masa 1"
                viewModel.resetPaymentState()
                snackbarHostState.showSnackbar(paymentSuccessMsg)
            }
            is PaymentState.Error -> {
                showPaymentDialog = false
                val msg = state.message
                viewModel.resetPaymentState()
                snackbarHostState.showSnackbar(msg)
            }
            else -> {}
        }
    }

    // Modern New Table Dialog
    if (showNewTableDialog) {
        var newTableNameInput by remember { mutableStateOf("Masa ${openOrders.size + 1}") }
        val quickTablePresets = listOf("Masa 1", "Masa 2", "Masa 3", "Masa 4", "Bahçe 1", "Bahçe 2", "Teras 1", "VIP")

        AlertDialog(
            onDismissRequest = { showNewTableDialog = false },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Surface(
                        shape = RoundedCornerShape(10.dp),
                        color = Color(0xFFEFF6FF),
                        modifier = Modifier.size(40.dp)
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Icon(Icons.Outlined.TableRestaurant, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(22.dp))
                        }
                    }
                    Spacer(Modifier.width(12.dp))
                    Column {
                        Text(l10n("Yeni Masa Aç"), fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFF0F172A))
                        Text("Adisyon başlatmak için masa seçin", fontSize = 12.sp, color = Color(0xFF64748B))
                    }
                }
            },
            text = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(14.dp)
                ) {
                    OutlinedTextField(
                        value = newTableNameInput,
                        onValueChange = { newTableNameInput = it },
                        label = { Text(l10n("Masa Adı / No")) },
                        placeholder = { Text("Örn: Masa 5 veya Bahçe 2") },
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1),
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color(0xFFF8FAFC)
                        )
                    )

                    // Quick Table Preset Chips
                    Column {
                        Text("Hızlı Masa Seçenekleri", fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF475569))
                        Spacer(Modifier.height(6.dp))
                        Row(
                            modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
                            horizontalArrangement = Arrangement.spacedBy(6.dp)
                        ) {
                            quickTablePresets.forEach { preset ->
                                val isSelected = newTableNameInput == preset
                                Surface(
                                    shape = RoundedCornerShape(8.dp),
                                    color = if (isSelected) Color(0xFFEFF6FF) else Color(0xFFF8FAFC),
                                    border = BorderStroke(1.dp, if (isSelected) Color(0xFF2563EB) else Color(0xFFE2E8F0)),
                                    modifier = Modifier.clickable { newTableNameInput = preset }
                                ) {
                                    Text(
                                        text = preset,
                                        fontSize = 12.sp,
                                        fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                                        color = if (isSelected) Color(0xFF2563EB) else Color(0xFF334155),
                                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp)
                                    )
                                }
                            }
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        val validName = newTableNameInput.ifBlank { "Masa ${openOrders.size + 1}" }
                        tableName = validName
                        showNewTableDialog = false
                        viewModel.createNewTableOrder(validName) {
                            showAdisyonDialog = true
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(6.dp))
                    Text(l10n("Masa Aç & Adisyona Git"), fontWeight = FontWeight.SemiBold)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showNewTableDialog = false },
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text(l10n("İptal"), color = Color(0xFF64748B), fontWeight = FontWeight.SemiBold)
                }
            }
        )
    }

    // Modern Manual Item Dialog
    if (showManualItemDialog) {
        var manualName by remember { mutableStateOf("") }
        var manualPrice by remember { mutableStateOf("") }
        AlertDialog(
            onDismissRequest = { showManualItemDialog = false },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Surface(
                        shape = RoundedCornerShape(10.dp),
                        color = Color(0xFFEFF6FF),
                        modifier = Modifier.size(40.dp)
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Icon(Icons.Outlined.AddShoppingCart, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(22.dp))
                        }
                    }
                    Spacer(Modifier.width(12.dp))
                    Column {
                        Text(l10n("Manuel Ürün / Hizmet Ekle"), fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFF0F172A))
                        Text("Menü dışı özel sipariş girişi", fontSize = 12.sp, color = Color(0xFF64748B))
                    }
                }
            },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    OutlinedTextField(
                        value = manualName,
                        onValueChange = { manualName = it },
                        label = { Text(l10n("Ürün / Hizmet Adı")) },
                        placeholder = { Text("Örn: Özel Karışım Kokteyl") },
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1),
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color(0xFFF8FAFC)
                        )
                    )
                    OutlinedTextField(
                        value = manualPrice,
                        onValueChange = { manualPrice = it },
                        label = { Text(l10n("Birim Fiyat (₺)")) },
                        placeholder = { Text("0.00") },
                        shape = RoundedCornerShape(10.dp),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1),
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color(0xFFF8FAFC)
                        )
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        val price = manualPrice.toDoubleOrNull()
                        if (manualName.isNotBlank() && price != null) {
                            viewModel.addManualItem(manualName, price)
                            showManualItemDialog = false
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(10.dp),
                    enabled = manualName.isNotBlank() && manualPrice.toDoubleOrNull() != null
                ) {
                    Text(l10n("Adisyona Ekle"), fontWeight = FontWeight.SemiBold)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showManualItemDialog = false },
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text(l10n("İptal"), color = Color(0xFF64748B), fontWeight = FontWeight.SemiBold)
                }
            }
        )
    }

    // Modern Table Name Edit Dialog
    if (showTableNameDialog) {
        var tempName by remember { mutableStateOf(tableName) }
        AlertDialog(
            onDismissRequest = { showTableNameDialog = false },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = {
                Text(l10n("Masa Adını Düzenle"), fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFF0F172A))
            },
            text = {
                OutlinedTextField(
                    value = tempName,
                    onValueChange = { tempName = it },
                    label = { Text(l10n("Masa Adı")) },
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1),
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color(0xFFF8FAFC)
                    )
                )
            },
            confirmButton = {
                Button(
                    onClick = {
                        tableName = tempName
                        showTableNameDialog = false
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text(l10n("Kaydet"), fontWeight = FontWeight.SemiBold)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showTableNameDialog = false },
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text(l10n("İptal"), color = Color(0xFF64748B), fontWeight = FontWeight.SemiBold)
                }
            }
        )
    }

    // Modern Payment Dialog
    if (showPaymentDialog) {
        val totalAmount = cart.entries.sumOf { it.key.price * it.value }

        AlertDialog(
            onDismissRequest = { showPaymentDialog = false },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Surface(
                        shape = RoundedCornerShape(10.dp),
                        color = Color(0xFFEFF6FF),
                        modifier = Modifier.size(42.dp)
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Icon(Icons.Outlined.Payments, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(24.dp))
                        }
                    }
                    Spacer(Modifier.width(12.dp))
                    Column {
                        Text(l10n("Adisyon Ödeme"), fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFF0F172A))
                        Text("Toplam Ödenecek: ₺${totalAmount.toInt()}", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Color(0xFF2563EB))
                    }
                }
            },
            text = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(14.dp)
                ) {
                    // Payment Method Switcher
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        Surface(
                            shape = RoundedCornerShape(10.dp),
                            color = if (paymentMethod == "CASH") Color(0xFFEFF6FF) else Color(0xFFF8FAFC),
                            border = BorderStroke(1.dp, if (paymentMethod == "CASH") Color(0xFF2563EB) else Color(0xFFE2E8F0)),
                            modifier = Modifier
                                .weight(1f)
                                .clickable { paymentMethod = "CASH" }
                        ) {
                            Column(
                                modifier = Modifier.padding(12.dp),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Icon(Icons.Outlined.CreditCard, contentDescription = null, tint = if (paymentMethod == "CASH") Color(0xFF2563EB) else Color(0xFF64748B), modifier = Modifier.size(22.dp))
                                Spacer(Modifier.height(4.dp))
                                Text("Nakit / Kart", fontWeight = FontWeight.Bold, fontSize = 12.sp, color = if (paymentMethod == "CASH") Color(0xFF2563EB) else Color(0xFF334155))
                            }
                        }

                        Surface(
                            shape = RoundedCornerShape(10.dp),
                            color = if (paymentMethod == "ROOM") Color(0xFFEFF6FF) else Color(0xFFF8FAFC),
                            border = BorderStroke(1.dp, if (paymentMethod == "ROOM") Color(0xFF2563EB) else Color(0xFFE2E8F0)),
                            modifier = Modifier
                                .weight(1f)
                                .clickable { paymentMethod = "ROOM" }
                        ) {
                            Column(
                                modifier = Modifier.padding(12.dp),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Icon(Icons.Outlined.Hotel, contentDescription = null, tint = if (paymentMethod == "ROOM") Color(0xFF2563EB) else Color(0xFF64748B), modifier = Modifier.size(22.dp))
                                Spacer(Modifier.height(4.dp))
                                Text("Odaya Yaz (Folio)", fontWeight = FontWeight.Bold, fontSize = 12.sp, color = if (paymentMethod == "ROOM") Color(0xFF2563EB) else Color(0xFF334155))
                            }
                        }
                    }

                    if (paymentMethod == "ROOM") {
                        var roomSearchQuery by remember { mutableStateOf("") }
                        val filteredReservations = remember(activeReservations, roomSearchQuery) {
                            if (roomSearchQuery.isBlank()) {
                                activeReservations
                            } else {
                                activeReservations.filter { res ->
                                    val roomNo = res.rooms?.roomNumber ?: ""
                                    val fullName = "${res.guests?.firstName ?: ""} ${res.guests?.lastName ?: ""}"
                                    roomNo.contains(roomSearchQuery, ignoreCase = true) ||
                                            fullName.contains(roomSearchQuery, ignoreCase = true)
                                }
                            }
                        }

                        Column {
                            Text("Oda ve Misafir Seçimi", fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF475569))
                            Spacer(Modifier.height(6.dp))

                            OutlinedTextField(
                                value = roomSearchQuery,
                                onValueChange = { roomSearchQuery = it },
                                placeholder = { Text("Oda no veya misafir adı...", fontSize = 13.sp) },
                                leadingIcon = { Icon(Icons.Outlined.Search, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(18.dp)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                shape = RoundedCornerShape(10.dp),
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = Color(0xFF2563EB),
                                    unfocusedBorderColor = Color(0xFFCBD5E1),
                                    focusedContainerColor = Color.White,
                                    unfocusedContainerColor = Color(0xFFF8FAFC)
                                )
                            )

                            Spacer(Modifier.height(8.dp))

                            if (filteredReservations.isEmpty()) {
                                Text(
                                    if (activeReservations.isEmpty()) "Konaklayan aktif misafir bulunamadı." else "Eşleşen oda/misafir bulunamadı.",
                                    color = Color(0xFF94A3B8),
                                    fontSize = 13.sp,
                                    modifier = Modifier.padding(vertical = 8.dp)
                                )
                            } else {
                                LazyColumn(modifier = Modifier.heightIn(max = 180.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                                    items(filteredReservations) { res ->
                                        val isSelected = res.id == selectedReservationId
                                        val roomNo = res.rooms?.roomNumber ?: "Oda ${res.roomId ?: "?"}"
                                        val guestFirstName = res.guests?.firstName ?: ""
                                        val guestLastName = res.guests?.lastName ?: ""
                                        val guestInfo = if (guestFirstName.isNotBlank() || guestLastName.isNotBlank()) {
                                            "$guestFirstName $guestLastName".trim()
                                        } else {
                                            "Misafir"
                                        }

                                        Surface(
                                            shape = RoundedCornerShape(8.dp),
                                            color = if (isSelected) Color(0xFFEFF6FF) else Color(0xFFF8FAFC),
                                            border = BorderStroke(1.dp, if (isSelected) Color(0xFF2563EB) else Color(0xFFE2E8F0)),
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .clickable { selectedReservationId = res.id }
                                        ) {
                                            Row(
                                                modifier = Modifier.padding(10.dp),
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                RadioButton(
                                                    selected = isSelected,
                                                    onClick = { selectedReservationId = res.id },
                                                    colors = RadioButtonDefaults.colors(selectedColor = Color(0xFF2563EB))
                                                )
                                                Spacer(Modifier.width(8.dp))
                                                Column {
                                                    Text(text = "Oda $roomNo", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFF0F172A))
                                                    Text(text = guestInfo, fontSize = 12.sp, color = Color(0xFF64748B))
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        if (paymentMethod == "CASH") {
                            viewModel.takePayment(tableName, null)
                        } else {
                            if (selectedReservationId != null) {
                                viewModel.takePayment(tableName, selectedReservationId)
                            }
                        }
                    },
                    enabled = (paymentMethod == "CASH") || (paymentMethod == "ROOM" && selectedReservationId != null),
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(10.dp)
                ) {
                    if (paymentState == PaymentState.Loading) {
                        CircularProgressIndicator(color = Color.White, modifier = Modifier.size(18.dp))
                    } else {
                        Text(l10n("Ödemeyi Tamamla"), fontWeight = FontWeight.Bold)
                    }
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showPaymentDialog = false },
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text(l10n("İptal"), color = Color(0xFF64748B), fontWeight = FontWeight.SemiBold)
                }
            }
        )
    }

    // Full-Screen Modern Adisyon Dialog
    if (showAdisyonDialog) {
        Dialog(
            onDismissRequest = {
                viewModel.saveCurrentOrder(tableName)
                showAdisyonDialog = false
            },
            properties = DialogProperties(usePlatformDefaultWidth = false)
        ) {
            Surface(
                modifier = Modifier.fillMaxSize(),
                color = Color(0xFFF8FAFC)
            ) {
                Column(modifier = Modifier.fillMaxSize()) {
                    // Modern Adisyon Top Header
                    Surface(
                        color = Color.White,
                        border = BorderStroke(1.dp, Color(0xFFE2E8F0))
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .clip(RoundedCornerShape(8.dp))
                                    .clickable { showTableNameDialog = true }
                                    .padding(4.dp)
                            ) {
                                Surface(
                                    shape = RoundedCornerShape(8.dp),
                                    color = Color(0xFFEFF6FF),
                                    modifier = Modifier.size(36.dp)
                                ) {
                                    Box(contentAlignment = Alignment.Center) {
                                        Icon(Icons.Outlined.TableRestaurant, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(20.dp))
                                    }
                                }
                                Spacer(modifier = Modifier.width(10.dp))
                                Column {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Text(tableName, color = Color(0xFF0F172A), fontWeight = FontWeight.Bold, fontSize = 16.sp)
                                        Spacer(Modifier.width(6.dp))
                                        Icon(Icons.Default.Edit, contentDescription = l10n("Düzenle"), tint = Color(0xFF64748B), modifier = Modifier.size(14.dp))
                                    }
                                    Text("Açık Sipariş Adisyonu", fontSize = 11.sp, color = Color(0xFF64748B))
                                }
                            }

                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                                OutlinedButton(
                                    onClick = {
                                        viewModel.saveCurrentOrder(tableName)
                                        showAdisyonDialog = false
                                    },
                                    colors = ButtonDefaults.outlinedButtonColors(contentColor = Color(0xFF475569)),
                                    border = BorderStroke(1.dp, Color(0xFFCBD5E1)),
                                    shape = RoundedCornerShape(8.dp)
                                ) {
                                    Icon(Icons.Outlined.Save, contentDescription = null, modifier = Modifier.size(16.dp))
                                    Spacer(Modifier.width(6.dp))
                                    Text("Kaydet & Kapat", fontSize = 13.sp, fontWeight = FontWeight.Medium)
                                }
                                IconButton(onClick = {
                                    viewModel.saveCurrentOrder(tableName)
                                    showAdisyonDialog = false
                                }) {
                                    Icon(Icons.Default.Close, contentDescription = l10n("Kapat"), tint = Color(0xFF475569))
                                }
                            }
                        }
                    }

                    // Main Content (Menu on Left, Cart on Right)
                    Row(modifier = Modifier.fillMaxWidth().weight(1f)) {
                        // Left 60%: Menu & Categories
                        Column(
                            modifier = Modifier
                                .weight(0.6f)
                                .fillMaxHeight()
                                .padding(16.dp)
                        ) {
                            // Category Bar & Search
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(
                                    modifier = Modifier
                                        .weight(1f)
                                        .horizontalScroll(rememberScrollState()),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    PosCategoryChip(
                                        text = l10n("Tümü"),
                                        isSelected = selectedCategoryId == "ALL",
                                        onClick = { selectedCategoryId = "ALL" }
                                    )
                                    categories.forEach { category ->
                                        PosCategoryChip(
                                            text = l10n(category.name),
                                            isSelected = category.id == selectedCategoryId,
                                            onClick = { selectedCategoryId = category.id }
                                        )
                                    }
                                }

                                Spacer(Modifier.width(10.dp))

                                Button(
                                    onClick = { showManualItemDialog = true },
                                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFEFF6FF), contentColor = Color(0xFF2563EB)),
                                    border = BorderStroke(1.dp, Color(0xFFDBEAFE)),
                                    shape = RoundedCornerShape(8.dp),
                                    contentPadding = PaddingValues(horizontal = 10.dp, vertical = 6.dp)
                                ) {
                                    Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp))
                                    Spacer(Modifier.width(4.dp))
                                    Text("Manuel Ürün", fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                                }
                            }

                            Spacer(Modifier.height(14.dp))

                            val filteredItems = if (selectedCategoryId == "ALL") items else items.filter { it.category_id == selectedCategoryId }
                            
                            if (filteredItems.isEmpty()) {
                                Box(
                                    modifier = Modifier.fillMaxSize(),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Icon(Icons.Outlined.RestaurantMenu, contentDescription = null, tint = Color(0xFF94A3B8), modifier = Modifier.size(48.dp))
                                        Spacer(Modifier.height(8.dp))
                                        Text("Bu kategoride ürün bulunmuyor", color = Color(0xFF64748B), fontSize = 14.sp)
                                    }
                                }
                            } else {
                                val gridState = androidx.compose.foundation.lazy.grid.rememberLazyGridState()
                                LazyVerticalGrid(
                                    state = gridState,
                                    columns = GridCells.Adaptive(minSize = 140.dp),
                                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                                    verticalArrangement = Arrangement.spacedBy(10.dp),
                                    modifier = Modifier.fillMaxSize().verticalScrollbar(gridState)
                                ) {
                                    items(filteredItems) { item ->
                                        PosMenuItemModernCard(
                                            item = item,
                                            onClick = { viewModel.addToCart(item) }
                                        )
                                    }
                                }
                            }
                        }

                        // Right 40%: Order Cart & Total
                        Surface(
                            modifier = Modifier
                                .weight(0.4f)
                                .fillMaxHeight(),
                            color = Color.White,
                            border = BorderStroke(1.dp, Color(0xFFE2E8F0))
                        ) {
                            Column(modifier = Modifier.fillMaxSize()) {
                            // Cart Header
                            Surface(
                                color = Color(0xFFF8FAFC),
                                border = BorderStroke(1.dp, Color(0xFFE2E8F0))
                            ) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(14.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Outlined.ReceiptLong, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(20.dp))
                                        Spacer(Modifier.width(8.dp))
                                        Text("Adisyon Kalemleri", fontWeight = FontWeight.Bold, fontSize = 15.sp, color = Color(0xFF0F172A))
                                    }
                                    Surface(
                                        shape = RoundedCornerShape(6.dp),
                                        color = Color(0xFFEFF6FF)
                                    ) {
                                        Text(
                                            text = "${cart.values.sum()} Ürün",
                                            fontWeight = FontWeight.SemiBold,
                                            fontSize = 11.sp,
                                            color = Color(0xFF2563EB),
                                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
                                        )
                                    }
                                }
                            }

                            // Items List
                            if (cart.isEmpty()) {
                                Box(
                                    modifier = Modifier.weight(1f).fillMaxWidth(),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Icon(Icons.Outlined.ShoppingCart, contentDescription = null, tint = Color(0xFFCBD5E1), modifier = Modifier.size(42.dp))
                                        Spacer(Modifier.height(8.dp))
                                        Text("Adisyona henüz ürün eklenmedi", color = Color(0xFF94A3B8), fontSize = 13.sp)
                                    }
                                }
                            } else {
                                val cartListState = androidx.compose.foundation.lazy.rememberLazyListState()
                                LazyColumn(
                                    state = cartListState,
                                    modifier = Modifier.weight(1f).padding(12.dp).verticalScrollbar(cartListState),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    items(cart.toList()) { (item, quantity) ->
                                        Card(
                                            shape = RoundedCornerShape(10.dp),
                                            colors = CardDefaults.cardColors(containerColor = Color(0xFFF8FAFC)),
                                            border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            Row(
                                                modifier = Modifier.fillMaxWidth().padding(10.dp),
                                                horizontalArrangement = Arrangement.SpaceBetween,
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Column(modifier = Modifier.weight(1f)) {
                                                    Text(item.name, fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF0F172A), maxLines = 2, overflow = TextOverflow.Ellipsis)
                                                    Text("₺${item.price.toInt()} / adet", fontSize = 11.sp, color = Color(0xFF64748B))
                                                }

                                                Row(
                                                    verticalAlignment = Alignment.CenterVertically,
                                                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                                                ) {
                                                    Surface(
                                                        shape = RoundedCornerShape(6.dp),
                                                        color = Color.White,
                                                        border = BorderStroke(1.dp, Color(0xFFCBD5E1)),
                                                        modifier = Modifier.size(26.dp).clickable { viewModel.removeFromCart(item) }
                                                    ) {
                                                        Box(contentAlignment = Alignment.Center) {
                                                            Text("-", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Color(0xFF334155))
                                                        }
                                                    }

                                                    Text("$quantity", fontSize = 13.sp, fontWeight = FontWeight.Bold, color = Color(0xFF0F172A), modifier = Modifier.padding(horizontal = 6.dp))

                                                    Surface(
                                                        shape = RoundedCornerShape(6.dp),
                                                        color = Color.White,
                                                        border = BorderStroke(1.dp, Color(0xFFCBD5E1)),
                                                        modifier = Modifier.size(26.dp).clickable { viewModel.addToCart(item) }
                                                    ) {
                                                        Box(contentAlignment = Alignment.Center) {
                                                            Text("+", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Color(0xFF334155))
                                                        }
                                                    }

                                                    Spacer(Modifier.width(6.dp))

                                                    IconButton(
                                                        onClick = { viewModel.removeCartItemCompletely(item) },
                                                        modifier = Modifier.size(28.dp)
                                                    ) {
                                                        Icon(Icons.Default.Delete, contentDescription = l10n("Sil"), tint = Color(0xFFEF4444), modifier = Modifier.size(16.dp))
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }

                            // Subtotal and Payment Actions Footer
                            Surface(
                                color = Color.White,
                                border = BorderStroke(1.dp, Color(0xFFE2E8F0))
                            ) {
                                Column(modifier = Modifier.padding(14.dp)) {
                                    val total = cart.entries.sumOf { it.key.price * it.value }
                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                        Text("Toplam Tutar", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = Color(0xFF0F172A))
                                        Text("₺${total.toInt()}", fontWeight = FontWeight.Bold, fontSize = 20.sp, color = Color(0xFF2563EB))
                                    }
                                    Spacer(modifier = Modifier.height(12.dp))
                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                                        OutlinedButton(
                                            onClick = {
                                                viewModel.saveCurrentOrder(tableName)
                                                showAdisyonDialog = false
                                            },
                                            modifier = Modifier.weight(1f).height(44.dp),
                                            shape = RoundedCornerShape(10.dp),
                                            border = BorderStroke(1.dp, Color(0xFFCBD5E1))
                                        ) {
                                            Text(l10n("Beklet"), fontSize = 13.sp, color = Color(0xFF475569), fontWeight = FontWeight.SemiBold)
                                        }
                                        Button(
                                            onClick = { showPaymentDialog = true },
                                            modifier = Modifier.weight(1.4f).height(44.dp),
                                            enabled = cart.isNotEmpty(),
                                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                                            shape = RoundedCornerShape(10.dp)
                                        ) {
                                            Icon(Icons.Outlined.Payments, contentDescription = null, modifier = Modifier.size(18.dp))
                                            Spacer(Modifier.width(6.dp))
                                            Text(l10n("Ödeme Al"), fontSize = 13.sp, fontWeight = FontWeight.Bold)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        }
    }

    // MAIN SCREEN CONTENT: Clean Open Tables Dashboard
    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                border = BorderStroke(1.dp, Color(0xFFE2E8F0))
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 8.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = l10n("Geri"),
                            tint = Color(0xFF0F172A)
                        )
                    }
                    Spacer(Modifier.width(4.dp))
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            l10n("Restoran POS & Adisyon"),
                            fontWeight = FontWeight.Bold,
                            fontSize = 18.sp,
                            color = Color(0xFF0F172A)
                        )
                        Text(
                            "Masa siparişleri ve anlık adisyon yönetimi",
                            fontSize = 12.sp,
                            color = Color(0xFF64748B)
                        )
                    }
                    LanguageSelectorDropdown()
                    Box {
                        IconButton(onClick = { showTopMenu = !showTopMenu }) {
                            Icon(Icons.Default.Menu, contentDescription = l10n("Menü"), tint = Color(0xFF0F172A))
                        }
                        DropdownMenu(
                            expanded = showTopMenu,
                            onDismissRequest = { showTopMenu = false },
                            modifier = Modifier.background(Color.White)
                        ) {
                            DropdownMenuItem(
                                text = { Text(l10n("+ Yeni Masa Aç"), fontWeight = FontWeight.SemiBold) },
                                onClick = {
                                    showTopMenu = false
                                    showNewTableDialog = true
                                },
                                leadingIcon = { Icon(Icons.Default.Add, contentDescription = null, tint = Color(0xFF2563EB)) }
                            )
                            DropdownMenuItem(
                                text = { Text(l10n("+ Manuel Ürün Ekle"), fontWeight = FontWeight.SemiBold) },
                                onClick = {
                                    showTopMenu = false
                                    showManualItemDialog = true
                                },
                                leadingIcon = { Icon(Icons.Outlined.AddShoppingCart, contentDescription = null, tint = Color(0xFF64748B)) }
                            )
                        }
                    }
                }
            }
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .padding(16.dp)
        ) {
            // Metrics Summary Bar
            Card(
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(14.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Surface(
                            shape = RoundedCornerShape(10.dp),
                            color = Color(0xFFEFF6FF),
                            modifier = Modifier.size(42.dp)
                        ) {
                            Box(contentAlignment = Alignment.Center) {
                                Icon(Icons.Outlined.TableRestaurant, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(22.dp))
                            }
                        }
                        Spacer(modifier = Modifier.width(12.dp))
                        Column {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Text(l10n("Açık Masalar"), fontWeight = FontWeight.Bold, fontSize = 16.sp, color = Color(0xFF0F172A))
                                Spacer(Modifier.width(8.dp))
                                Surface(
                                    shape = RoundedCornerShape(6.dp),
                                    color = Color(0xFFEFF6FF),
                                    border = BorderStroke(1.dp, Color(0xFFDBEAFE))
                                ) {
                                    Text(
                                        text = "${openOrders.size} Masa Aktif",
                                        fontSize = 11.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = Color(0xFF2563EB),
                                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp)
                                    )
                                }
                            }
                            val totalActiveSum = openOrders.sumOf { it.total_amount }
                            Text("Toplam Adisyon Tutarı: ₺${totalActiveSum.toInt()}", fontSize = 13.sp, color = Color(0xFF64748B))
                        }
                    }

                    Button(
                        onClick = { showNewTableDialog = true },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                        shape = RoundedCornerShape(10.dp),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 10.dp)
                    ) {
                        Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                        Spacer(modifier = Modifier.width(6.dp))
                        Text(l10n("Yeni Masa Aç"), fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                    }
                }
            }

            Spacer(Modifier.height(16.dp))

            // Main Body: Responsive Grid of Table Cards
            val tableGridState = androidx.compose.foundation.lazy.grid.rememberLazyGridState()
            LazyVerticalGrid(
                state = tableGridState,
                columns = GridCells.Adaptive(minSize = 160.dp),
                horizontalArrangement = Arrangement.spacedBy(14.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp),
                modifier = Modifier.fillMaxSize().verticalScrollbar(tableGridState)
            ) {
                // Card 1: Add New Table Button Card
                item {
                    Surface(
                        shape = RoundedCornerShape(16.dp),
                        color = Color(0xFFEFF6FF),
                        border = BorderStroke(1.dp, Color(0xFF93C5FD)),
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(130.dp)
                            .clickable { showNewTableDialog = true }
                    ) {
                        Column(
                            modifier = Modifier.fillMaxSize().padding(16.dp),
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Surface(
                                shape = CircleShape,
                                color = Color.White,
                                modifier = Modifier.size(40.dp)
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    Icon(Icons.Default.Add, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(22.dp))
                                }
                            }
                            Spacer(modifier = Modifier.height(8.dp))
                            Text(l10n("+ Yeni Masa Aç"), fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFF1E40AF))
                        }
                    }
                }

                // Cards 2..N: Open Table Cards
                items(openOrders) { order ->
                    val isSelected = order.id == currentOrderId || order.table_name == tableName
                    Card(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(130.dp)
                            .clickable {
                                viewModel.loadOrder(order)
                                tableName = order.table_name
                                showAdisyonDialog = true
                            },
                        colors = CardDefaults.cardColors(containerColor = Color.White),
                        border = BorderStroke(1.5.dp, if (isSelected) Color(0xFF2563EB) else Color(0xFFE2E8F0)),
                        shape = RoundedCornerShape(16.dp)
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(14.dp),
                            verticalArrangement = Arrangement.SpaceBetween
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Surface(
                                        shape = RoundedCornerShape(8.dp),
                                        color = Color(0xFFEFF6FF),
                                        modifier = Modifier.size(32.dp)
                                    ) {
                                        Box(contentAlignment = Alignment.Center) {
                                            Icon(Icons.Outlined.TableRestaurant, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(18.dp))
                                        }
                                    }
                                    Spacer(Modifier.width(8.dp))
                                    Text(
                                        order.table_name,
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 14.sp,
                                        color = Color(0xFF0F172A),
                                        maxLines = 1,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                }
                                Surface(
                                    shape = RoundedCornerShape(6.dp),
                                    color = Color(0xFFEFF6FF)
                                ) {
                                    Text(
                                        l10n("AÇIK"),
                                        fontSize = 10.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = Color(0xFF2563EB),
                                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                    )
                                }
                            }

                            Column {
                                Text("Adisyon Tutarı", fontSize = 11.sp, color = Color(0xFF94A3B8))
                                Text(
                                    "₺${order.total_amount.toInt()}",
                                    fontSize = 18.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFF0F172A)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun PosCategoryChip(text: String, isSelected: Boolean, onClick: () -> Unit) {
    Surface(
        shape = RoundedCornerShape(8.dp),
        color = if (isSelected) Color(0xFFEFF6FF) else Color.White,
        border = BorderStroke(1.dp, if (isSelected) Color(0xFF2563EB) else Color(0xFFCBD5E1)),
        modifier = Modifier.clickable { onClick() }
    ) {
        Text(
            text = text,
            color = if (isSelected) Color(0xFF2563EB) else Color(0xFF475569),
            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
            fontSize = 12.sp,
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp)
        )
    }
}

@Composable
fun PosMenuItemModernCard(item: PosItem, onClick: () -> Unit) {
    Card(
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() }
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Surface(
                shape = CircleShape,
                color = Color(0xFFF8FAFC),
                modifier = Modifier.size(42.dp)
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(Icons.Outlined.Fastfood, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(20.dp))
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                item.name,
                fontWeight = FontWeight.SemiBold,
                fontSize = 13.sp,
                color = Color(0xFF0F172A),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                "₺${item.price.toInt()}",
                color = Color(0xFF2563EB),
                fontWeight = FontWeight.Bold,
                fontSize = 14.sp
            )
        }
    }
}